import DataToolbar from '@/components/DataToolbar';
import DatePeriodPicker from '@/components/DatePeriodPicker';
import WriteButton from "@/components/WriteButton";
import PurchaseWorkspace from "@/components/PurchaseWorkspace";
import PurchaseHistoryDetail from "@/components/PurchaseHistoryDetail";
import { moneyText, importedPurchaseLine } from "@/lib/purchase-workspace.mjs";
import { useAuth } from "@/context/AuthContext";
import { hasModuleAccess } from "@/lib/user-permissions";
import { purchaseDraftPrefix, readPurchaseDrafts, removePurchaseDraft, removeLocalPurchaseDraft } from "@/lib/purchase-drafts";
import { useCallback, useEffect, useState, useRef } from "react";
import { transactionPeriod } from '@/lib/transaction-period.mjs';
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { paymentLabel, historyDate } from '@/lib/purchase-history.mjs';
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Plus, Trash2, ShoppingBag, ChevronRight, Upload, FileDown, FileSpreadsheet, Loader2,
} from "lucide-react";

const statusBadge = {
  lunas: "bg-brand-soft text-brand-readable",
  utang: "bg-warning-soft text-warning-readable",
  sebagian: "bg-warning-soft text-warning-readable",
};
const paymentTypeLabel = { tunai: "Tunai", transfer: "Transfer", utang: "Utang", campuran: "Campuran" };

export default function Purchases() {
  const { tenant, user, readOnly } = useAuth();
  const canReadPurchases = hasModuleAccess(user, 'purchases', 'read');
  const canWritePurchases = hasModuleAccess(user, 'purchases', 'write');
  const canDeletePurchases = hasModuleAccess(user, 'purchases', 'delete');
  const location = useLocation();
  const draftPrefix = purchaseDraftPrefix(tenant?.id, user?.id);
  const [drafts, setDrafts] = useState([]);
  const [draftError, setDraftError] = useState("");
  const [editingDraft, setEditingDraft] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [purchaseTab, setPurchaseTab] = useState('pembelian');
  const [visibleCount, setVisibleCount] = useState(50);
  const [period, setPeriod] = useState(() => 'all');
  const [customDates, setCustomDates] = useState(() => transactionPeriod('today'));
  const range = period === 'custom' ? customDates : transactionPeriod(period);
  const invalidPeriod = period === 'custom' && (!range.start || !range.end || range.start > range.end);
  const periodQuery = new URLSearchParams({ ...(range.start ? { from_date: range.start } : {}), ...(range.end ? { to_date: range.end } : {}) }).toString();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const loadSequence = useRef(0), loadedCount = useRef(0);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [importLines, setImportLines] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const refreshDrafts = async () => {
    if (readOnly || !canReadPurchases || !tenant?.id || !user?.id) return;
    try {
      const result = await readPurchaseDrafts(draftPrefix);
      setDrafts(result.drafts); setDraftError(result.warning);
    } catch (e) { setDraftError(`Draf server belum dapat dimuat: ${apiError(e)}`); }
  };
  useEffect(() => {
    if (open) return;
    refreshDrafts();
    let stopped = false, running = false;
    const changed = async () => {
      if (stopped || running || document.hidden || !navigator.onLine) return;
      running = true;
      try { await refreshDrafts(); } finally { running = false; }
    };
    window.addEventListener("focus", changed);
    window.addEventListener("online", changed);
    const timer = setInterval(changed, 15000);
    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", changed);
      window.removeEventListener("online", changed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPrefix, open, readOnly, canReadPurchases]);
  const openNew = () => { setEditingDraft(null); setImportLines(null); setOpen(true); };
  const closeForm = () => { setOpen(false); setImportLines(null); setEditingDraft(null); };
  const deleteDraft = async (draft) => {
    const linked = Boolean(draft.warehouse_receipt_ids?.length);
    const message = linked
      ? `Batalkan draf Pembelian ${draft.supplierName || ''}? Penerimaan Gudang yang tertaut juga akan dibatalkan. Draf belum menambah stok; data barang tetap tersimpan di histori pembatalan.`
      : "Hapus draf pembelian ini? Isian draf tidak dapat dikembalikan.";
    if (!window.confirm(message)) return;
    try {
      if (draft.recoverySourceId) removeLocalPurchaseDraft(draftPrefix, draft.recoverySourceId);
      else await removePurchaseDraft(draft);
      await refreshDrafts();
      toast.success(linked ? 'Draf Pembelian dan Penerimaan Gudang dibatalkan. Stok tidak berubah.' : 'Draf Pembelian dihapus.');
    } catch (e) { toast.error(`Draf gagal dihapus: ${apiError(e)}`); }
  };

  const load = useCallback(async (more = false) => {
    const sequence = ++loadSequence.current;
    if (!more) { setPurchases([]); loadedCount.current = 0; setHasMore(false); }
    if (invalidPeriod) { setLoading(false); return; }
    setLoading(true); setLoadError('');
    try {
      const pu = await api.get(`/purchases?${periodQuery}&limit=200&offset=${more ? loadedCount.current : 0}`);
      if (sequence !== loadSequence.current) return;
      setPurchases(previous => more ? [...previous, ...pu.data] : pu.data);
      loadedCount.current = (more ? loadedCount.current : 0) + pu.data.length;
      setHasMore(pu.data.length === 200);
    } catch (e) { if (sequence === loadSequence.current) { setLoadError(apiError(e)); toast.error(apiError(e)); } }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [periodQuery, invalidPeriod]);
  useEffect(() => {
    const requestCounter = loadSequence;
    load();
    return () => { requestCounter.current++; };
  }, [load]);
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([api.get('/products', { signal: controller.signal }), api.get('/suppliers', { signal: controller.signal })])
      .then(([pr, su]) => { if (!controller.signal.aborted) { setProducts(pr.data); setSuppliers(su.data); } })
      .catch(error => { if (!controller.signal.aborted) toast.error(`Katalog pembelian: ${apiError(error)}`); });
    return () => controller.abort();
  }, [catalogRevision]);
  const paymentIds = purchases.filter(p => p.payment_managed_by === 'akunkas').map(p => p.id).join(',');
  const purchaseRows = [...purchases].sort((a,b) => String(b.date || b.created_at || '').localeCompare(String(a.date || a.created_at || '')));
  const paidRows = purchases.filter(p => paymentLabel(p) === 'Lunas').sort((a,b) => {
    const paidAt = p => p.payment_history?.at(-1)?.date || p.payment_history?.at(-1)?.at || p.paid_at || p.date || p.created_at || '';
    return String(paidAt(b)).localeCompare(String(paidAt(a)));
  });
  const openRows = purchaseRows.filter(p => !['Lunas', 'Batal', 'Retur'].includes(paymentLabel(p)));
  const returnRows = purchases.flatMap(p => (p.returns || []).map(record => ({ purchase: p, record })))
    .sort((a,b) => String(b.record.at || b.record.return_date || '').localeCompare(String(a.record.at || a.record.return_date || '')));
  const displayedRows = purchaseTab === 'lunas' ? paidRows : purchaseTab === 'retur' ? returnRows : openRows;
  const displayedCount = displayedRows.length;
  const visibleRows = displayedRows.slice(0, visibleCount);
  useEffect(() => { setVisibleCount(50); }, [purchaseTab, periodQuery]);
  useEffect(() => {
    if (open || !paymentIds) return;
    let stopped = false, running = false;
    const refreshPayments = async () => {
      if (running || document.hidden || !navigator.onLine) return;
      running = true;
      try {
        const ids = paymentIds.split(',');
        for (let i = 0; i < ids.length && !stopped; i += 100) {
          const { data } = await api.get('/purchases/payment-status', { params: { ids: ids.slice(i, i + 100).join(',') } });
          if (stopped) return;
          const patches = new Map(data.map(p => [p.id, p]));
          setPurchases(rows => rows.map(p => patches.has(p.id) ? { ...p, ...patches.get(p.id) } : p));
          setSelected(p => p && patches.has(p.id) ? { ...p, ...patches.get(p.id) } : p);
        }
      } catch { /* Existing verification time remains visible; never invent payment success. */ }
      finally { running = false; }
    };
    const timer = setInterval(refreshPayments, 30000);
    window.addEventListener('focus', refreshPayments);
    window.addEventListener('online', refreshPayments);
    return () => { stopped = true; clearInterval(timer); window.removeEventListener('focus', refreshPayments); window.removeEventListener('online', refreshPayments); };
  }, [paymentIds, open]);
  useEffect(() => {
    const openId = location.state?.openPurchaseId;
    if (!openId || !purchases.length) return;
    const found = purchases.find((p) => p.id === openId);
    if (found) setSelected(found);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, purchases]);

  useEffect(() => {
    const id = location.state?.openDraftId;
    if (!id || open) return;
    const draft = drafts.find(d => d.id === id);
    if (!draft) return;
    setEditingDraft(draft); setImportLines(null); setOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, drafts, open, navigate]);

  const setStatus = async (p, status) => {
    try {
      const { data } = await api.put(`/purchases/${p.id}/payment?status=${status}`);
      toast.success("Status pembayaran diperbarui");
      load();
      setSelected((s) => (s && s.id === p.id ? data : s));
    } catch (e) { toast.error(apiError(e)); }
  };
  const applyReturnedPurchase = (updated) => {
    setSelected(updated);
    setPurchases((current) => current.map((purchase) => purchase.id === updated.id ? updated : purchase));
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/purchases/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`${data.errors.length} baris dilewati: ${data.errors.slice(0, 3).join('; ')}${data.errors.length > 3 ? '; periksa baris lainnya di Excel' : ''}`, { duration: 12000 });
      if (!data.items?.length) { toast.error("Tidak ada baris valid pada file Excel"); return; }
      const lines = data.items.map(importedPurchaseLine);
      setImportLines(lines);
      setEditingDraft(null);
      setOpen(true);
      toast.success(`${lines.length} barang dimuat dari Excel — lengkapi supplier lalu simpan`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  const draftSection = !readOnly && canReadPurchases && <section data-testid="purchase-drafts" className="mb-3 overflow-hidden rounded-lg border border-ui-line bg-ui-card">
    <div className="flex items-center justify-between gap-2 border-b border-ui-line bg-ui-soft px-3 py-2 text-sm font-semibold">Draf Pembelian <span className="rounded-full bg-ui-card px-2 py-0.5 text-xs font-medium">{drafts.length}</span></div>
    {draftError && <p role="alert" className="px-3 py-2 text-xs text-danger-readable">{draftError}</p>}
    {!drafts.length && !draftError && <p className="px-3 py-2 text-xs text-ui-muted">Belum ada draf pembelian.</p>}
    {[...drafts].sort((a,b) => String(b.date || b.updatedAt || '').localeCompare(String(a.date || a.updatedAt || ''))).map((draft) => <div key={draft.id} data-testid={`purchase-draft-${draft.id}`} className="flex min-w-0 items-center justify-between gap-2 border-t border-ui-line px-3 py-2 first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{draft.supplierName || "Supplier belum diisi"}{draft.invoiceNo ? ` · ${draft.invoiceNo}` : ""}</div>
        <div className="truncate text-xs text-ui-muted">{draft.date || 'Tanggal belum diatur'} · {draft.lines.length} barang{draft.updated_by_name ? ` · diubah ${draft.updated_by_name}` : ''}{draft.warehouse_receipt_ids?.length ? ' · dari Penerimaan Gudang' : ''}{draft.destinationStoreName ? ` · ${draft.destinationStoreName}` : ''}{draft.recoverySourceId ? ' · salinan lokal belum tersimpan di server' : ''}</div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {canWritePurchases && <WriteButton data-testid={`resume-purchase-draft-${draft.id}`} variant="outline" className="h-8 px-2 text-xs" onClick={() => { setImportLines(null); setEditingDraft(draft); setOpen(true); }}>Lanjutkan</WriteButton>}
        {canDeletePurchases && <WriteButton data-testid={`delete-purchase-draft-${draft.id}`} variant="outline" className={`h-8 ${draft.warehouse_receipt_ids?.length ? 'px-2 text-xs text-danger-readable border-danger/40' : 'w-8 p-0'}`} onClick={() => deleteDraft(draft)} aria-label={draft.warehouse_receipt_ids?.length ? 'Batalkan draf pembelian dan penerimaan gudang' : 'Hapus draf pembelian'}>{draft.warehouse_receipt_ids?.length ? 'Batalkan' : <Trash2 className="h-4 w-4" />}</WriteButton>}
      </div>
    </div>)}
  </section>;
  const table = <>
    <div className="rounded-xl border border-ui-line overflow-x-auto" data-testid={purchaseTab === 'retur' ? 'purchase-returns-table' : 'purchases-table'}>
      <table className="w-full min-w-[540px] text-sm">
        <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide"><tr>
          <th className="text-left p-3">{purchaseTab === 'retur' ? 'No. Retur / Faktur' : 'No. Faktur'}</th>
          <th className="text-left p-3">Supplier</th><th className="text-left p-3 hidden md:table-cell">{purchaseTab === 'retur' ? 'Tanggal Retur' : purchaseTab === 'lunas' ? 'Tanggal Pembayaran' : 'Tanggal Faktur'}</th>
          <th className="text-center p-3">Status</th><th className="text-right p-3">{purchaseTab === 'retur' ? 'Nilai Retur' : 'Total'}</th><th className="p-3"></th>
        </tr></thead>
        <tbody>{visibleRows.map((row) => {
          const isReturn = purchaseTab === 'retur';
          const p = isReturn ? row.purchase : row;
          const record = isReturn ? row.record : null;
          const date = isReturn ? record.at || (record.return_date ? `${record.return_date.slice(0,4)}-${record.return_date.slice(4,6)}-${record.return_date.slice(6,8)}` : p.date) : purchaseTab === 'lunas' ? p.payment_history?.at(-1)?.date || p.payment_history?.at(-1)?.at || p.paid_at || p.date : p.date;
          return <tr key={isReturn ? record.id || record.return_no : p.id} data-testid={isReturn ? `purchase-return-row-${record.id || record.return_no}` : `purchase-row-${p.id}`} onClick={() => setSelected(p)} className="border-t border-ui-line hover:bg-ui-soft cursor-pointer">
            <td className="p-3 font-mono">{isReturn ? <>{record.return_no}<div className="text-[10px] text-ui-muted">{p.invoice_no}</div></> : p.invoice_no}<div className="md:hidden text-[10px] text-ui-muted">{date ? historyDate(date) : '—'}</div></td>
            <td className="p-3">{p.supplier_name}</td><td className="p-3 hidden md:table-cell text-ui-muted">{date ? historyDate(date) : '—'}</td>
            <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full ${isReturn ? 'bg-danger-soft text-danger-readable' : statusBadge[p.payment_status] || statusBadge.utang}`}>{isReturn ? 'Retur Pembelian' : p.payment_managed_by === 'akunkas' ? paymentLabel(p) : paymentTypeLabel[p.payment_type] || p.payment_status}</span>{!isReturn && p.payment_status === 'sebagian' && <div className="text-[10px] text-ui-muted mt-1">Sebagian utang</div>}{!isReturn && p.return_total > 0 && <div className="text-[10px] text-danger-readable mt-1">RETUR {rupiah(p.return_total)}</div>}{isReturn && record.resolution && <div className="text-[10px] text-ui-muted mt-1">{record.resolution === 'debt_reduction' ? 'Potong utang' : 'Pengembalian'}</div>}</td>
            <td className="p-3 text-right font-mono font-bold text-accent-readable">{isReturn ? rupiah(record.total) : p.calculation ? moneyText(p.net_total) : rupiah(p.net_total)}{!isReturn && p.return_total > 0 && <div className="text-[10px] font-normal text-ui-muted line-through">{rupiah(p.total)}</div>}</td>
            <td className="p-3 text-ui-muted"><ChevronRight className="h-4 w-4" /></td>
          </tr>;
        })}
        {visibleRows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-ui-muted"><ShoppingBag className="h-8 w-8 mx-auto mb-2" />{loading ? 'Memuat pembelian…' : invalidPeriod ? 'Periksa rentang tanggal' : loadError ? 'Data gagal dimuat' : purchaseTab === 'lunas' ? 'Belum ada pembelian lunas pada periode ini' : purchaseTab === 'retur' ? 'Belum ada retur pembelian pada periode ini' : 'Tidak ada pembelian belum lunas pada periode ini'}</td></tr>}
        </tbody>
      </table>
    </div>
    {(visibleCount < displayedCount || hasMore) && <div className="mt-3 text-center"><Button variant="outline" disabled={loading} onClick={async () => {
      if (visibleCount < displayedCount) { setVisibleCount(value => value + 50); return; }
      const before = loadedCount.current;
      await load(true);
      if (loadedCount.current > before) setVisibleCount(value => value + 50);
    }}>{visibleCount < displayedCount ? 'Tampilkan 50 baris berikutnya' : 'Muat 50 baris berikutnya'}</Button></div>}
  </>;

  return (
    <>
      <PageHeader title="Pembelian Barang" subtitle={`${displayedCount} ${purchaseTab === 'retur' ? 'retur pembelian' : 'faktur ditampilkan'}`}>
        
      </PageHeader>

      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5">
        <Tabs value={purchaseTab} onValueChange={setPurchaseTab} className="space-y-3">
        <TabsList aria-label="Daftar pembelian" data-testid="purchase-tabs" className="w-full max-w-full overflow-x-auto h-auto flex flex-nowrap justify-start gap-1 bg-ui-card border border-ui-line p-1">
          <TabsTrigger value="pembelian" data-testid="purchase-tab-open" className="h-10">Pembelian ({openRows.length}{hasMore ? '+' : ''})</TabsTrigger>
          <TabsTrigger value="lunas" data-testid="purchase-tab-paid" className="h-10">Lunas ({paidRows.length}{hasMore ? '+' : ''})</TabsTrigger>
          <TabsTrigger value="retur" data-testid="purchase-tab-returns" className="h-10">Retur Pembelian ({returnRows.length}{hasMore ? '+' : ''})</TabsTrigger>
        </TabsList>
        <DataToolbar label="Alat tabel pembelian" className="mb-3" actions={<div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
          <Button data-testid="download-template-btn" variant="outline" onClick={() => downloadFile("/purchases/template", "template_pembelian.xlsx")} className="border-ui-line bg-transparent hover:bg-ui-card gap-2">
            <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">Template</span>
          </Button>
          {canWritePurchases && <WriteButton data-testid="import-excel-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-brand/50 bg-transparent hover:bg-brand-soft text-brand-readable gap-2">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} <span className="hidden sm:inline">Import Excel</span>
          </WriteButton>}
          <Button data-testid="export-purchases-btn" variant="outline" disabled={invalidPeriod} onClick={() => downloadFile(`/purchases/export?${periodQuery}`, "riwayat_pembelian.xlsx")} className="border-ui-line bg-transparent hover:bg-ui-card gap-2">
            <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">Export</span>
          </Button>
          {canWritePurchases && <WriteButton data-testid="add-purchase-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Input Pembelian</span>
          </WriteButton>}
        </div>}>
<section aria-label="Periode pembelian" className="flex min-w-0 flex-wrap items-end gap-2">
          <div className="hidden min-w-0 sm:block"><DatePeriodPicker start={range.start} end={range.end} allowAll label="Periode pembelian" testId="purchase-date-filter" onChange={next => { setCustomDates(next); setPeriod(next.start ? 'custom' : 'all'); }} /></div>
          <div className="text-xs text-ui-muted sm:hidden"><span>Periode</span>
            <select aria-label="Periode pembelian" value={period} onChange={event => setPeriod(event.target.value)} className="block h-10 mt-1 sm:mt-0 rounded-md border border-ui-line bg-ui-card text-ui-text px-3">
              <option value="today">Hari ini</option><option value="yesterday">Kemarin</option><option value="this-month">Bulan ini</option><option value="all">Semua</option><option value="custom">Tanggal sendiri</option>
            </select>
          </div>
          {period === 'custom' && <div className="grid grid-cols-2 gap-2 w-full sm:hidden">
            <label className="text-xs text-ui-muted min-w-0">Dari<input aria-label="Tanggal awal pembelian" type="date" value={customDates.start} max={customDates.end || undefined} onChange={event => setCustomDates(value => ({ ...value, start: event.target.value }))} className="block mt-1 h-10 w-full min-w-0 rounded-md border border-ui-line bg-ui-card text-ui-text px-2" /></label>
            <label className="text-xs text-ui-muted min-w-0">Sampai<input aria-label="Tanggal akhir pembelian" type="date" value={customDates.end} min={customDates.start || undefined} onChange={event => setCustomDates(value => ({ ...value, end: event.target.value }))} className="block mt-1 h-10 w-full min-w-0 rounded-md border border-ui-line bg-ui-card text-ui-text px-2" /></label>
          </div>}
          <span role="status" className="text-xs text-ui-muted pb-2">{loading ? 'Memuat…' : `${Math.min(visibleCount, displayedCount)} dari ${displayedCount}${hasMore ? '+' : ''} ${purchaseTab === 'retur' ? 'retur' : 'faktur'}`} · tanggal {purchaseTab === 'retur' ? 'retur' : purchaseTab === 'lunas' ? 'pembayaran' : 'faktur'} (WIB)</span>
          {invalidPeriod && <p role="alert" className="w-full text-xs text-danger-readable">Isi rentang tanggal yang valid; tanggal awal tidak boleh setelah tanggal akhir.</p>}
          {loadError && <p role="alert" className="w-full text-xs text-danger-readable">{loadError} <button type="button" onClick={() => load()} className="underline">Coba lagi</button></p>}
        </section>
</DataToolbar>
        <TabsContent value="pembelian" className="mt-0">{draftSection}{table}</TabsContent>
        <TabsContent value="lunas" className="mt-0">{table}</TabsContent>
        <TabsContent value="retur" className="mt-0">{table}</TabsContent>
        </Tabs>
      </div>

      {open && <PurchaseWorkspace onClose={closeForm} initialLines={importLines} initialDraft={editingDraft} draftPrefix={draftPrefix} products={products} suppliers={suppliers} onSupplierCreated={(supplier) => setSuppliers((previous) => [...previous, supplier].sort((a, b) => a.name.localeCompare(b.name)))} onSaved={() => { load(); setCatalogRevision(value => value + 1); }} />}

      {selected && <PurchaseHistoryDetail purchase={selected} products={products} onClose={() => setSelected(null)} onStatusChange={setStatus} onReturned={applyReturnedPurchase} readOnly={readOnly} />}

    </>
  );
}
